import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /**
   * Variant responses keyed by request URL, shared by every picker on the page,
   * so an option that was preloaded (or picked before) switches instantly.
   * @type {Map<string, Promise<string>>}
   */
  static #responseCache = new Map();

  /** Incremented per selection so a slower, older response can't overwrite a newer one. */
  #requestId = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));

    if (this.#isOnProductPage) {
      // Warm the other options once the page has settled, and again on hover/touch/focus
      const warm = () => ('requestIdleCallback' in window ? requestIdleCallback : setTimeout)(() => this.#prefetchOptions());
      if (document.readyState === 'complete') warm();
      else window.addEventListener('load', warm, { once: true });

      this.addEventListener('pointerover', this.#handleOptionIntent);
      this.addEventListener('focusin', this.#handleOptionIntent);
      this.addEventListener('touchstart', this.#handleOptionIntent, { passive: true });
    }
  }

  get #isOnProductPage() {
    return (
      this.dataset.templateProductMatch === 'true' && !this.closest('product-card') && !this.closest('quick-add-dialog')
    );
  }

  /**
   * Limits the response to this picker's own section instead of the whole page
   * (~160 KB instead of ~575 KB). Every variant:update listener only reads
   * elements inside that section.
   * @param {string} requestUrl
   * @returns {string}
   */
  #withSectionId(requestUrl) {
    const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
    if (!sectionId) return requestUrl;

    return `${requestUrl}${requestUrl.includes('?') ? '&' : '?'}section_id=${sectionId}`;
  }

  /**
   * The request URL the picker would use if `input` were selected.
   * @param {HTMLInputElement} input
   * @returns {string | undefined}
   */
  #requestUrlFor(input) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl || !input.dataset.optionValueId) return;

    // Options that load another product (combined listings) re-render all of main; skip them
    const connectedUrl = input.dataset.connectedProductUrl;
    if (connectedUrl && connectedUrl !== productUrl.split('?')[0]) return;

    const values = [];
    for (const group of this.querySelectorAll('fieldset, select')) {
      const chosen = group.contains(input) ? input : group.querySelector('option[selected], input:checked');
      if (!(chosen instanceof HTMLElement) || !chosen.dataset.optionValueId) return;
      values.push(chosen.dataset.optionValueId);
    }

    return this.#withSectionId(`${productUrl}?option_values=${values.join(',')}`);
  }

  /** Preloads the responses for the currently unselected options (capped to keep it light). */
  #prefetchOptions() {
    const inputs = Array.from(this.querySelectorAll('fieldset input:not(:checked)')).slice(0, 8);
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      const url = this.#requestUrlFor(input);
      if (url) this.#getResponseText(url).catch(() => {});
    }
  }

  /** @param {Event} event */
  #handleOptionIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('label');
    const input = label?.control ?? label?.querySelector('input') ?? event.target.closest('input');
    if (!(input instanceof HTMLInputElement) || input.checked) return;

    const url = this.#requestUrlFor(input);
    if (url) this.#getResponseText(url).catch(() => {});
  };

  /**
   * Fetches a response once and shares it; failed requests aren't cached.
   * @param {string} url
   * @returns {Promise<string>}
   */
  #getResponseText(url) {
    const cache = VariantPicker.#responseCache;
    let pending = cache.get(url);

    if (!pending) {
      pending = fetch(url).then((response) => {
        if (!response.ok) throw new Error(`Variant request failed (${response.status})`);
        return response.text();
      });
      cache.set(url, pending);
      pending.catch(() => cache.delete(url));
    }

    return pending;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    let requestUrl = this.buildRequestUrl(selectedOption);
    // Combined-listing switches morph all of <main>, so they still need the full page
    if (isOnProductPage && !loadsNewProduct) requestUrl = this.#withSectionId(requestUrl);

    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    const requestId = ++this.#requestId;

    this.#getResponseText(requestUrl)
      .then((responseText) => {
        // A newer selection was made while this one loaded; let that one win
        if (requestId !== this.#requestId) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }

        // Warm the options that are unselected now, so the next switch is instant too
        if (this.#isOnProductPage) this.#prefetchOptions();
      })
      .catch((error) => console.error(error));
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
